import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

/**
 * Equipment rentals hooks. Read from the brix-order local mirror
 * (orders.v_equipment_* views) populated nightly by the
 * sync-equipment-rentals Netlify function.
 */

export interface EquipmentAssetRow {
  id: string;
  contractId: string | null;
  assetTag: string | null;
  serialNumber: string | null;
  qboItemName: string | null;
  description: string | null;
  category: string | null;
  make: string | null;
  modelNumber: string | null;
  qty: number | null;
  monthlyRent: number;
  ownershipType: string | null;
  status: string | null;
  isLoaner: boolean | null;
  installedAt: string | null;
  removedAt: string | null;
  catalogItemId: string | null;
  imageUrl: string | null;
  specSheetUrl: string | null;
  usefulLifeMonths: number | null;
  catalogName: string | null;
  vendor: string | null;
}

interface AssetDbRow {
  id: string;
  contract_id: string | null;
  asset_tag: string | null;
  serial_number: string | null;
  qbo_item_name: string | null;
  description: string | null;
  category: string | null;
  make: string | null;
  model_number: string | null;
  qty: number | null;
  monthly_rent: number | string | null;
  ownership_type: string | null;
  status: string | null;
  is_loaner: boolean | null;
  installed_at: string | null;
  removed_at: string | null;
  catalog_item_id: string | null;
  image_url: string | null;
  spec_sheet_url: string | null;
  useful_life_months: number | null;
  catalog_name: string | null;
  vendor: string | null;
}

export interface EquipmentContractRow {
  id: string;
  contractNumber: string | null;
  contractType: string | null;
  category: string | null;
  status: string | null;
  startDate: string | null;
  endDate: string | null;
  monthlyEquipmentAmount: number;
  monthlyServiceAmount: number;
  signatureStatus: string | null;
  signedAt: string | null;
  externalDocumentUrl: string | null;
}

interface ContractDbRow {
  id: string;
  contract_number: string | null;
  contract_type: string | null;
  category: string | null;
  status: string | null;
  start_date: string | null;
  end_date: string | null;
  monthly_equipment_amount: number | string | null;
  monthly_service_amount: number | string | null;
  signature_status: string | null;
  signed_at: string | null;
  external_document_url: string | null;
}

export interface EquipmentSummary {
  assetCount: number;
  monthlyRentTotal: number;
}

export function useEquipmentAssets() {
  return useQuery<EquipmentAssetRow[]>({
    queryKey: ['equipment', 'assets'],
    queryFn: async () => {
      const { data, error } = await supabase
        .schema('orders')
        .from('v_equipment_assets')
        .select(
          'id, contract_id, asset_tag, serial_number, qbo_item_name, description, category, make, model_number, qty, monthly_rent, ownership_type, status, is_loaner, installed_at, removed_at, catalog_item_id, image_url, spec_sheet_url, useful_life_months, catalog_name, vendor',
        )
        .order('installed_at', { ascending: false });
      if (error) return [];
      return (data ?? []).map((r): EquipmentAssetRow => {
        const row = r as AssetDbRow;
        return {
          id: row.id,
          contractId: row.contract_id,
          assetTag: row.asset_tag,
          serialNumber: row.serial_number,
          qboItemName: row.qbo_item_name,
          description: row.description,
          category: row.category,
          make: row.make,
          modelNumber: row.model_number,
          qty: row.qty,
          monthlyRent: Number(row.monthly_rent ?? 0),
          ownershipType: row.ownership_type,
          status: row.status,
          isLoaner: row.is_loaner,
          installedAt: row.installed_at,
          removedAt: row.removed_at,
          catalogItemId: row.catalog_item_id,
          imageUrl: row.image_url,
          specSheetUrl: row.spec_sheet_url,
          usefulLifeMonths: row.useful_life_months,
          catalogName: row.catalog_name,
          vendor: row.vendor,
        };
      });
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useEquipmentContracts() {
  return useQuery<EquipmentContractRow[]>({
    queryKey: ['equipment', 'contracts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .schema('orders')
        .from('v_equipment_contracts')
        .select(
          'id, contract_number, contract_type, category, status, start_date, end_date, monthly_equipment_amount, monthly_service_amount, signature_status, signed_at, external_document_url',
        )
        .order('start_date', { ascending: false });
      if (error) return [];
      return (data ?? []).map((r): EquipmentContractRow => {
        const row = r as ContractDbRow;
        return {
          id: row.id,
          contractNumber: row.contract_number,
          contractType: row.contract_type,
          category: row.category,
          status: row.status,
          startDate: row.start_date,
          endDate: row.end_date,
          monthlyEquipmentAmount: Number(row.monthly_equipment_amount ?? 0),
          monthlyServiceAmount: Number(row.monthly_service_amount ?? 0),
          signatureStatus: row.signature_status,
          signedAt: row.signed_at,
          externalDocumentUrl: row.external_document_url,
        };
      });
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useEquipmentSummary() {
  return useQuery<EquipmentSummary>({
    queryKey: ['equipment', 'summary'],
    queryFn: async () => {
      const { data, error } = await supabase
        .schema('orders')
        .from('v_equipment_summary')
        .select('asset_count, monthly_rent_total')
        .maybeSingle();
      if (error || !data) return { assetCount: 0, monthlyRentTotal: 0 };
      const row = data as { asset_count: number; monthly_rent_total: number | string };
      return {
        assetCount: row.asset_count ?? 0,
        monthlyRentTotal: Number(row.monthly_rent_total ?? 0),
      };
    },
    staleTime: 5 * 60 * 1000,
  });
}
